import Shimmer from '@/components/ui/Shimmer';
import SectionTitle, { SectionTitleProps } from '@/components/ui/Titles/SectionTitle';
import { Fragment, useEffect, useRef } from 'react';
import TagButton from '@/components/ui/Buttons/TagButton';
import ProductList from '@/components/ui/ProductList';
import ProductCarousel, { ProductCarouselProps } from '@/components/ui/Carousel/ProductCarousel';
import { forwardRef } from 'react';
import Link from '@/components/ui/Link';
import ScrollArea from '@/components/ui/ScrollArea';
import classnames from 'classnames';
import { BasicContainerBlockProps } from './BasicContentRowContainerBlock';
import { BasicContentRowBlockType, getFeedIdentifier } from './util';
import { FilterBlock } from '@/@types/content';
import { ISearchResultItem } from '@/@types/client-api';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';

export type BasicContentRowFilter = FilterBlock & {
  id?: string;
  selected?: boolean;
};

type BasicContentRowProps = BasicContainerBlockProps & {
  isLoading?: boolean;
  isFirstLoad?: boolean;
  isLoadingFilter?: boolean;
  sectionTitle?: SectionTitleProps;
  productCarouselProps?: ProductCarouselProps;
  filterProps?: {
    handleFilterClick?: (filter: BasicContentRowFilter) => void;
    filters?: BasicContentRowFilter[];
    selectedFilter?: BasicContentRowFilter;
    showSingleVisibleFilter?: boolean;
  };
  searchResultsItems?: ISearchResultItem[];
  filteredItems?: Record<string, unknown>[];
  onNoResults?: (feedId: string) => void;
};

const BasicContentRow = forwardRef<HTMLDivElement, BasicContentRowProps>(
  (
    {
      isLoading,
      isFirstLoad,
      isLoadingFilter,
      sectionTitle,
      displayContentAsList,
      type,
      enumerateCards,
      findMoreLabel,
      seeAllLink,
      filterProps,
      productCarouselProps,
      searchResultsItems,
      filteredItems,
      onNoResults
    },
    ref
  ) => {
    const stateSiteLabels = useSelector((state: RootState) => state.page.siteLabels);
    const { filters, handleFilterClick, selectedFilter, showSingleVisibleFilter } =
      filterProps ?? {};
    const hasCalledNoResults = useRef(false);

    // Get feed identifier - prefer id, fallback to text, then value
    const currentFeedId = getFeedIdentifier(selectedFilter ?? {});
    const carouselCards =
      (productCarouselProps?.cards as Record<string, unknown>[] | undefined) ?? [];
    const hasCarouselCards = carouselCards.length > 0;
    const shouldHandleNoResults =
      !isLoading &&
      (!hasCarouselCards || !searchResultsItems?.length) &&
      !isFirstLoad &&
      !!onNoResults &&
      !!currentFeedId;

    useEffect(() => {
      // Reset the flag when filter changes or loading starts
      if (isLoading || isFirstLoad) {
        hasCalledNoResults.current = false;
      }
    }, [isLoading, isFirstLoad, currentFeedId]);

    useEffect(() => {
      if (!shouldHandleNoResults || !onNoResults || !currentFeedId || hasCalledNoResults.current) {
        return;
      }

      hasCalledNoResults.current = true;
      onNoResults(currentFeedId);
    }, [shouldHandleNoResults, onNoResults, currentFeedId]);

    if (shouldHandleNoResults) {
      return null;
    }

    return (
      <div
        ref={ref}
        className="container flex flex-col gap-8 overflow-x-clip"
        data-component={'BasicContentRow'}
      >
        {!!isLoading && !!isFirstLoad ? (
          <Shimmer className="h-14 w-1/3" />
        ) : (
          !!sectionTitle && (
            <SectionTitle
              propertyNameAvatar=""
              propertyNameIcon=""
              propertyNameTitle="Heading"
              propertyNameSubcopy=""
              elementType="h2"
              {...sectionTitle}
            >
              {!!seeAllLink && (
                <Link variant="bold-link-with-icon" icon="chevron-right" {...seeAllLink} />
              )}
            </SectionTitle>
          )
        )}

        {filters &&
        (((filters?.length > 1 || showSingleVisibleFilter) && !displayContentAsList) ||
          type == BasicContentRowBlockType.Library) ? (
          <ScrollArea
            orientation="horizontal"
            className="relative left-1/2 ml-[-50vw] w-screen overflow-visible"
            barClassName="!-bottom-3 px-6"
          >
            <div className="container flex gap-2">
              {filters.map((button, index) => {
                return (
                  <Fragment key={index}>
                    {isFirstLoad || isLoadingFilter ? (
                      <Shimmer className="h-14 w-40" />
                    ) : (
                      <TagButton
                        styleType={button.text === selectedFilter?.text ? 'black' : 'outline'}
                        isActive={button.text === selectedFilter?.text}
                        label={button.text}
                        disabled={isLoading}
                        onClick={() => {
                          if (handleFilterClick) {
                            handleFilterClick(button);
                          }
                        }}
                        type="button"
                        className={classnames({
                          '!border-black': button.text === selectedFilter?.text
                        })}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>
          </ScrollArea>
        ) : null}

        {!isLoading && !hasCarouselCards && !isFirstLoad && (
          <div>
            <p className="text-1">{stateSiteLabels?.noResultsLabel}</p>
          </div>
        )}

        {displayContentAsList && !!filteredItems && filteredItems.length > 0 ? (
          <ProductList
            cards={filteredItems}
            isLoading={isLoading}
            seeMoreLabel={findMoreLabel}
            listLimit={4}
            enumerateCards={enumerateCards}
          />
        ) : !displayContentAsList && (isLoading || hasCarouselCards) ? (
          !!productCarouselProps && <ProductCarousel {...productCarouselProps} />
        ) : null}
      </div>
    );
  }
);

BasicContentRow.displayName = 'BasicContentRow';
export default BasicContentRow;
